import React from 'react';
import { useTranslatedContent } from '../../i18n/hooks/useTranslatedContent';
import { Cloud, Shield, Share2, Lock, Users, Database, Workflow, Sparkles } from 'lucide-react';
import Button from '../common/Button';

const NextcloudSection = () => {
  const { t } = useTranslatedContent('nextcloud');

  return (
    <div id="nextcloud" className="relative py-24 overflow-hidden">
      {/* Modern gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900 via-indigo-900 to-violet-900">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'30\' height=\'30\' viewBox=\'0 0 30 30\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Ccircle cx=\'1.22676\' cy=\'1.22676\' r=\'1.22676\' fill=\'rgba(255,255,255,0.1)\'/%3E%3C/svg%3E')] opacity-20" />
      </div>

      {/* Animated orbs */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 -left-48 w-96 h-96 bg-blue-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob" />
        <div className="absolute top-1/3 -right-48 w-96 h-96 bg-indigo-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute -bottom-48 left-1/2 w-96 h-96 bg-violet-500/30 rounded-full mix-blend-multiply filter blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-500/10 border border-blue-400/20 backdrop-blur-sm mb-8">
            <Cloud className="h-5 w-5 text-blue-400 mr-2" />
            <span className="text-blue-200 font-medium">Enterprise Cloud Platform</span>
          </div>
          
          <h2 className="text-4xl font-bold text-white mb-6">{t('title')}</h2>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {t('subtitle')}
          </p>
        </div>

        {/* Main Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {/* Collaboration Hub */}
          <div className="relative group">
            <div className="absolute inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
            <div className="relative bg-black/50 backdrop-blur-sm rounded-xl p-8">
              <div className="flex items-center mb-6">
                <Share2 className="h-8 w-8 text-blue-400 mr-3" />
                <h3 className="text-xl font-semibold text-white">Collaboration Hub</h3>
              </div>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="p-2 bg-blue-500/20 rounded-lg mr-4">
                    <Users className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Team Collaboration</h4>
                    <p className="text-blue-100">Real-time document editing, comments, and version control</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="p-2 bg-blue-500/20 rounded-lg mr-4">
                    <Workflow className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Workflow Integration</h4>
                    <p className="text-blue-100">Custom approval flows and automated task management</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Security Features */}
          <div className="relative group">
            <div className="absolute inset-0.5 bg-gradient-to-r from-indigo-500 to-violet-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-500"></div>
            <div className="relative bg-black/50 backdrop-blur-sm rounded-xl p-8">
              <div className="flex items-center mb-6">
                <Shield className="h-8 w-8 text-indigo-400 mr-3" />
                <h3 className="text-xl font-semibold text-white">Enterprise Security</h3>
              </div>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="p-2 bg-indigo-500/20 rounded-lg mr-4">
                    <Lock className="h-5 w-5 text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Advanced Protection</h4>
                    <p className="text-blue-100">End-to-end encryption and compliance features</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="p-2 bg-indigo-500/20 rounded-lg mr-4">
                    <Database className="h-5 w-5 text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-white mb-2">Data Governance</h4>
                    <p className="text-blue-100">Advanced retention policies and audit logging</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {[
            {
              icon: Cloud,
              title: 'Enterprise File Sharing',
              description: 'Secure and scalable file sharing with advanced collaboration features'
            },
            {
              icon: Shield,
              title: 'Data Security',
              description: 'End-to-end encryption and compliance with data protection regulations'
            },
            {
              icon: Share2,
              title: 'Collaboration Tools',
              description: 'Integrated tools for document editing and team collaboration'
            },
            {
              icon: Lock,
              title: 'Access Control',
              description: 'Fine-grained access management and sharing policies'
            },
            {
              icon: Database,
              title: 'Storage Management',
              description: 'Advanced quota management and storage optimization'
            },
            {
              icon: Sparkles,
              title: 'Smart Features',
              description: 'AI-powered search and content recommendations'
            }
          ].map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="group relative bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-violet-500 rounded-lg">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-blue-100">{feature.description}</p>

                {/* Decorative elements */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-blue-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-4 left-4 w-2 h-2 bg-violet-400 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            );
          })}
        </div>

        {/* Benefits Section */}
        <div className="bg-white/5 backdrop-blur-sm rounded-3xl border border-white/10 p-8 lg:p-12 mb-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl font-semibold text-white mb-4">Why Choose Our Nextcloud Solutions?</h3>
            <p className="text-blue-100">Get the most out of your Nextcloud deployment with our expert services</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Enterprise Focus',
                description: 'Tailored solutions for enterprise-scale deployments'
              },
              {
                title: 'Complete Integration',
                description: 'Seamless integration with your existing IT infrastructure'
              },
              {
                title: 'Expert Support',
                description: 'Dedicated support team with extensive Nextcloud expertise'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <h4 className="text-lg font-semibold text-white mb-2">{benefit.title}</h4>
                <p className="text-blue-100">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center">
          <Button
            variant="secondary"
            className="bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white border-0"
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {t('hero.buttonText')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NextcloudSection;